import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import SignedLayout from '../layouts/signed';
import AdminLayout from '../layouts/main/AdminLayout';
// components
import LoadingScreen from '../components/LoadingScreen';
import { AuthProvider } from '../contexts/AuthContext';
import ProtectedRoute from 'components/ProtectedRoute';
import ProfileSettings from 'layouts/dashboard/ProfileSettings';
import AdminProfileSettingsPage from 'components/_external-pages/admin/AdminProfileSettings';
import LedgerPage from '../pages/LedgerPage';
import Admin_UsersPage from '../pages/Admin_UsersPage';
import RegisterCourt from 'components/_external-pages/services/RegisterCourt';
import MyBookings from 'layouts/dashboard/ProfileMenuItems/MyBookings';
import ApprovalPage from '../pages/ApprovalPage';
import VerifyEmail from 'components/_external-pages/services/VerifyEmail';
import RefundManagementPage from '../pages/RefundManagementPage';
import CancellationManagementPage from '../pages/CancellationManagementPage';
import RefundHistoryPage from 'pages/RefundHistoryPage';
import CancellationHistoryPage from 'pages/CancellationHistoryPage';

// 
import ApproverApprovalPage from 'pages/ApproverPage/ApproverApprovalPage';
import ApproverLedgerPage from 'pages/ApproverPage/ApproverLedgerPage';
import ApproverCancellationManagementPage from 'pages/ApproverPage/ApproverCancellationManagementPage';
import ApproverCancellationHistoryPage from 'pages/ApproverPage/ApproverCancellationHistoryPage';
import ApproverDashboardPage from 'pages/ApproverPage/ApproverDashboardPage';


// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed',
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return (
    <AuthProvider>
      {useRoutes([
        // Main Routes
        {
          path: '*',
          element: <LogoOnlyLayout />,
          children: [
            { path: '404', element: <NotFound /> },
            { path: '*', element: <Navigate to="/404" replace /> },
          ],
        },
        {
          path: 'account',
          element: <SignedLayout />,
          children: [
            { path: '/', element: <Navigate to="/account/app" replace /> },
            { path: '/app', element: <MemberPage /> },
          ],
        },
        {
          path: '/',
          element: <MainLayout />,
          children: [
            { path: '/', element: <LandingPage /> },
            { path: '/about-us', element: <AboutPage /> },
            { path: '/services', element: <ServicesPage /> },
            { path: '/contact-us', element: <ContactPage /> },
            { path: '/register-court', element: <RegisterCourt /> },
            { path: '/verify-email', element: <VerifyEmail /> },
            { path: '/user/profile/settings', element: <ProfileSettings /> },
            { path: '/user/profile/bookings', element: <MyBookings /> },
          ],
        },
        {
          path: '/admin',
          element: <AdminLayout />,
          children: [
            { path: 'login', element: <LoginForm1 /> },
            {
              path: 'dashboard',
              element: (
                <ProtectedRoute requiredRole="admin">
                  <DashboardPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'approval',
              element: (
                <ProtectedRoute requiredRole="admin">
                  <ApprovalPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'ledger',
              element: (
                <ProtectedRoute requiredRole="admin">
                  <LedgerPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'manage-users',
              element: (
                <ProtectedRoute requiredRole="admin">
                  <Admin_UsersPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'manage-court',
              element: (
                <ProtectedRoute requiredRole="admin">
                  <ManageCourtPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'profile/settings',
              element: (
                <ProtectedRoute requiredRole="admin">
                  <AdminProfileSettingsPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'cancellations',
              element: (
                <ProtectedRoute requiredRole="admin">
                  <CancellationManagementPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'cancellations/history',
              element: (
                <ProtectedRoute requiredRole="admin">
                  <CancellationHistoryPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'refunds',
              element: (
                <ProtectedRoute requiredRole="admin">
                  <RefundManagementPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'refunds/history',
              element: (
                <ProtectedRoute requiredRole="admin">
                  <RefundHistoryPage />
                </ProtectedRoute>
              ),
            },
          ],
        },

        {
          path: '/approver',
          element: <AdminLayout />, // ✅ Reuse AdminLayout (or create ApproverLayout if needed)
          children: [
            { path: 'login', element: <LoginForm2 /> },
            {
              path: 'dashboard',
              element: (
                <ProtectedRoute requiredRole="approver">
                  <ApproverDashboardPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'approval',
              element: (
                <ProtectedRoute requiredRole="approver">
                  <ApproverApprovalPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'ledger',
              element: (
                <ProtectedRoute requiredRole="approver">
                  <ApproverLedgerPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'cancellations',
              element: (
                <ProtectedRoute requiredRole="approver">
                  <ApproverCancellationManagementPage />
                </ProtectedRoute>
              ),
            },
            {
              path: 'cancellations/history',
              element: (
                <ProtectedRoute requiredRole="approver">
                  <ApproverCancellationHistoryPage />
                </ProtectedRoute>
              ),
            },
          ]
        }

      ])}
    </AuthProvider>
  );
}

// IMPORT COMPONENTS
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')));
const AboutPage = Loadable(lazy(() => import('../pages/AboutPage')));
const ServicesPage = Loadable(lazy(() => import('../pages/ServicesPage')));
const ContactPage = Loadable(lazy(() => import('../pages/ContactPage')));
const InquiryPage = Loadable(lazy(() => import('../pages/InquiryPage')));
const LoginPage = Loadable(lazy(() => import('../pages/LoginPage')));
const MemberPage = Loadable(lazy(() => import('../pages/MemberPage')));
const DashboardPage = Loadable(lazy(() => import('../pages/DashboardPage')));
const ManageCourtPage = Loadable(lazy(() => import('../pages/ManageCourtPage')));
const LoginForm1 = Loadable(lazy(() => import('../components/_external-pages/admin/LoginForm1')));
const LoginForm2 = Loadable(lazy(() => import('../components/_external-pages/approver/LoginForm1')));
